import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Loader from '../../../../../Components/Utility/Loader/Loader';
import { BombiixModal } from '../../../../../Components/BombiixModal';
import { NewDatePicker } from '../../../../../Components/PIM/common/NewDatePicker';
import { NewInput } from '../../../../../Components/PIM/common/NewInput';
import '../sample-tracking-dates.scss';
import { unMOunt, getSingleSample } from '../../../../DevlopmentOrder/store/DevelopmentOrder.action';
import TrakingDates from '../TrakingDates/TrakingDates';
import {
  SamplesRootState,
  SampleTrackingDatesModalProps,
  TrackingDateField,
  SavedTrackingDate,
  SingleSampleResponse,
  RequestTypeOption,
} from '../store/Samples.type';

/**
 * Sample Tracking Dates — per-sample edit modal (Figma node 5205:510899).
 * Chrome comes from BombiixModal (blue Save left, centered title, red close right);
 * the body is a read-only Sample Ref / Supplier / Sample Type row (context for the
 * sample being edited), then Due Date, then the shared tracking-dates card. Those
 * three fields are display-only — the submit payload sources supplier/type from Redux
 * (`editDataForSampleTrackingDates`), never from this form.
 */
function SampleTrackingDatesModal({ onSubmitForm, show, id, toggle, devId, productBrand }: SampleTrackingDatesModalProps) {
  const { sampleTrakingDates } = useSelector((redState: SamplesRootState) => redState.Measuremets);
  const { isSupplierLoggedIn } = useSelector((redState: SamplesRootState) => redState.UserDetails);

  const [showLoader, setShowLoader] = useState(false);
  const [selectedRequestType, setSelectedRequestType] = useState<RequestTypeOption>({});
  const [sampleRef, setSampleRef] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [sampleTypeName, setSampleTypeName] = useState('');
  /* react-i18next@11.8 types t() as returning TFunctionResult, which @types/react@19 rejects
     as a ReactNode. TranslateFn is the shared cast declared in src/global.d.ts. */
  const { t: translate } = useTranslation('common');
  const t = translate as unknown as TranslateFn;

  const { devOrderId } = useParams();
  const devIds = devOrderId || devId;
  const dispatch = useDispatch();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({ mode: 'onBlur' });

  const {
    fields: trackingDatesFields,
    remove: trackingDatesRemove,
    append: trackingDatesAppend,
    update: trackingDatesUpdate,
  } = useFieldArray({ control, name: 'trackingDates' });

  useEffect(() => {
    return () => {
      dispatch(unMOunt());
    };
  }, [dispatch]);

  const onSubmitMethod = (data: Record<string, unknown>) => {
    setShowLoader(true);
    onSubmitForm({ ...data, trackingDates: (data.trackingDates as TrackingDateField[]) ?? [] });
    setShowLoader(false);
  };

  const getSingleSampleData = async (obj: Record<string, unknown>): Promise<SingleSampleResponse | undefined> => {
    return (await dispatch(getSingleSample(obj))) as unknown as SingleSampleResponse | undefined;
  };

  /**
   * Loads the sample and seeds the form: the due date, the request type (which locks the
   * Requested row), and one tracking-date row per active stage — merging any value already
   * saved against that stage onto the master's definition.
   */
  const editFormDataSet = async () => {
    if (!id) return;
    setShowLoader(true);
    const res = await getSingleSampleData({ sampleId: id, devOrderId: devIds });
    if (res) {
      setValue('dueDate', res.dueDate ? new Date(res.dueDate) : null, { shouldValidate: true });
      setSelectedRequestType({ value: res.requestType ?? '' });
      setSampleRef(res.sampleNumber ?? '');
      setSupplierName(res.supplier?.name ?? '');
      setSampleTypeName(res.sampleType?.name ?? '');

      const saved: TrackingDateField[] = ((res.trackingDates ?? []) as SavedTrackingDate[]).map((item) => ({
        ...item,
        name: item?.label?.replace('Date', '') ?? '',
        isTrakingURL: Boolean(item?.trackingUrl),
        value: item?.value ?? null,
      }));

      let trackingDatesToUse = sampleTrakingDates;
      const selectedBrandId = productBrand?.[0]?._id;
      if ((productBrand?.length ?? 0) > 0 && selectedBrandId && Array.isArray(sampleTrakingDates)) {
        trackingDatesToUse = sampleTrakingDates.filter((trackingDate) => Array.isArray(trackingDate.brands) && trackingDate.brands.includes(selectedBrandId));
      }

      const rows: TrackingDateField[] = [];
      trackingDatesToUse
        ?.filter((master: TrackingDateField) => master?.isActive || master?.sampleId?.isActive)
        .forEach((master: TrackingDateField) => {
          const match = saved.find((entry: TrackingDateField) => entry?.sampleId?._id === master?._id);
          rows.push(match ? { ...match, isChecked: Boolean(master.isChecked) } : master);
        });

      trackingDatesRemove();
      trackingDatesAppend([...rows]);
    }
    setShowLoader(false);
  };

  useEffect(() => {
    editFormDataSet();
  }, []);

  return (
    <BombiixModal
      show={show}
      title={t('SAMPLES.MODALPOPUP.SAMPLE_TRACKING_DATES_MODAL.SAMPLE_TRACKING_DATES')}
      onClose={toggle}
      onSave={handleSubmit(onSubmitMethod)}
      saveDisabled={showLoader}
      size="md"
    >
      {showLoader ? (
        <Loader />
      ) : (
        <form>
          <div className="sample-tracking-readonly-row">
            <div className="sample-tracking-col">
              <span className="bombiix-input__label">
                {t('SAMPLES.MODALPOPUP.SAMPLE_TRACKING_DATES_MODAL.SAMPLE_REF')}
                <span className="sample-tracking-required">*</span>
              </span>
              <NewInput value={sampleRef} disabled readOnly />
            </div>
            <div className="sample-tracking-col">
              <span className="bombiix-input__label">
                {t('SAMPLES.MODALPOPUP.SAMPLE_TRACKING_DATES_MODAL.SELECT_SUPPLIER')}
                <span className="sample-tracking-required">*</span>
              </span>
              <NewInput value={supplierName} disabled readOnly />
            </div>
            <div className="sample-tracking-col">
              <span className="bombiix-input__label">
                {t('SAMPLES.MODALPOPUP.SAMPLE_TRACKING_DATES_MODAL.SELECT_SAMPLE_TYPE')}
                <span className="sample-tracking-required">*</span>
              </span>
              <NewInput value={sampleTypeName} disabled readOnly />
            </div>
          </div>

          <div className="sample-tracking-field bombiix-input-wrap mb-3">
            <span className="bombiix-input__label">{t('SAMPLES.MODALPOPUP.SAMPLE_TRACKING_DATES_MODAL.DUE_DATE')}</span>
            <Controller
              name="dueDate"
              control={control}
              rules={{ required: selectedRequestType?.value === 'request' }}
              render={({ field }) => (
                <NewDatePicker
                  dateFormat="dd/MM/yyyy"
                  placeholder={t('DEVLOPMENT_ORDER.SAMPLES.MODAL_POPS.SAMPLE_TRACKING.DUE_DATE')}
                  popperPlacement="bottom-start"
                  disabled={Boolean(isSupplierLoggedIn)}
                  invalid={Boolean(errors?.dueDate)}
                  value={(field.value as Date | null) ?? null}
                  onChange={field.onChange}
                />
              )}
            />
            {errors?.dueDate && <small className="error-message">{t('SAMPLES.MODALPOPUP.SAMPLE_TRACKING_DATES_MODAL.EXPIRY_DATE_REQUIRED')}</small>}
          </div>

          <p className="bombiix-input__label">{t('SAMPLES.MODALPOPUP.SAMPLE_TRACKING_DATES_MODAL.SAMPLE_TRACKING_DATE')}</p>
          <TrakingDates
            trackingDatesFields={trackingDatesFields as unknown as TrackingDateField[]}
            trackingDatesRemove={trackingDatesRemove}
            trackingDatesUpdate={trackingDatesUpdate as unknown as (index: number, value: TrackingDateField) => void}
            selectedRequestType={selectedRequestType}
          />
        </form>
      )}
    </BombiixModal>
  );
}

export default SampleTrackingDatesModal;
